import { authenticatedFetch } from '@src/utility/authenticatedFetch';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import { err, ok } from './common/Result';

import type { Result, ServiceErrorCode } from './common/Result';
import type { FolderStructure } from '@src/types.ts/story';

const logger = LogManager.getLogger('service:FolderService');

export interface ServiceWarning {
  code: string;
  message: string;
}

export interface FolderMutationOk {
  structure: FolderStructure;
  warnings: ServiceWarning[];
}

export type DeleteMode = 'cascade' | 'promote';

interface ApiSuccess {
  structure: FolderStructure;
  warnings?: ServiceWarning[];
}

interface ApiError {
  error?: { code?: string; message?: string };
}

export class FolderService {
  constructor(
    private readonly apiUrl: string,
    private readonly getAuthToken: () => Promise<string | undefined>,
  ) {}

  async getFolderStructure(
    showcaseId: string,
  ): Promise<Result<FolderStructure>> {
    const url = `${this.apiUrl}/api/showcase/${showcaseId}/folders`;
    try {
      const response = await authenticatedFetch(
        url,
        { method: 'GET' },
        this.getAuthToken,
      );
      // Awaited so a body-read failure lands in the catch below.
      return await parseEnvelope(response, payload => payload.structure);
    } catch (error) {
      logger.error('Folder API GET failed', extractErrorInfo(error));
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Network error',
      );
    }
  }

  async createFolder(
    showcaseId: string,
    name: string,
    parentId: string | null,
  ): Promise<Result<FolderMutationOk>> {
    return this.mutate('POST', `/api/showcase/${showcaseId}/folders`, {
      name,
      parentId,
    });
  }

  async renameFolder(
    showcaseId: string,
    folderId: string,
    name: string,
  ): Promise<Result<FolderMutationOk>> {
    return this.mutate('PATCH', `/api/showcase/${showcaseId}/folders`, {
      op: 'rename',
      folderId,
      name,
    });
  }

  async deleteFolder(
    showcaseId: string,
    folderId: string,
    mode: DeleteMode,
  ): Promise<Result<FolderMutationOk>> {
    return this.mutate(
      'DELETE',
      `/api/showcase/${showcaseId}/folders/${folderId}?mode=${mode}`,
    );
  }

  async moveStoryToFolder(
    showcaseId: string,
    storyId: string,
    folderId: string | null,
  ): Promise<Result<FolderMutationOk>> {
    return this.mutate(
      'POST',
      `/api/showcase/${showcaseId}/folders/move-story`,
      { storyId, folderId },
    );
  }

  async moveStoriesToFolder(
    showcaseId: string,
    storyIds: string[],
    folderId: string | null,
  ): Promise<Result<FolderMutationOk>> {
    return this.mutate(
      'POST',
      `/api/showcase/${showcaseId}/folders/move-story`,
      { storyIds, folderId },
    );
  }

  private async mutate(
    method: 'POST' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
  ): Promise<Result<FolderMutationOk>> {
    const url = `${this.apiUrl}${path}`;
    try {
      const response = await authenticatedFetch(
        url,
        {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: body ? JSON.stringify(body) : undefined,
        },
        this.getAuthToken,
      );
      return await parseEnvelope(response, payload => ({
        structure: payload.structure,
        warnings: payload.warnings ?? [],
      }));
    } catch (error) {
      logger.error('Folder API request failed', extractErrorInfo(error));
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Network error',
      );
    }
  }
}

async function parseEnvelope<T>(
  response: Response,
  project: (payload: ApiSuccess) => T,
): Promise<Result<T>> {
  const text = await response.text();
  const payload = text ? safeParse(text) : null;
  if (response.ok) {
    if (!payload || typeof payload !== 'object' || !('structure' in payload)) {
      return err('EXTERNAL', 'Folder API returned an invalid response');
    }
    return ok(project(payload as ApiSuccess));
  }
  const errBody = (payload as ApiError | null)?.error;
  const code = mapStatusToCode(response.status, errBody?.code);
  const message =
    errBody?.message ?? `Folder API request failed (${response.status})`;
  return err(code, message);
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function mapStatusToCode(
  status: number,
  apiCode: string | undefined,
): ServiceErrorCode {
  if (apiCode === 'VALIDATION') return 'VALIDATION';
  if (apiCode === 'NOT_FOUND') return 'NOT_FOUND';
  if (status === 400) return 'VALIDATION';
  if (status === 404) return 'NOT_FOUND';
  if (status === 401) return 'UNAUTHENTICATED';
  if (status === 403) return 'PRECONDITION';
  return 'EXTERNAL';
}
